/**
 * FamilyService
 * Service for Family entity operations
 * Route: api/families
 * Controller: FamiliesController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * FamilyService
 * Handles family CRUD operations and member management
 * Maps to /api/families endpoint
 */
export class FamilyService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? FamilyService.DEFAULT_SERVICE_PATH,
            basePath: FamilyService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== OVERRIDE: GET BY ID ====================
    /**
     * Get family by ID with its members
     * GET /api/families/{id}
     * @param id - Family ID
     * @returns Family with members
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Family with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get family by ID with members (raw)
     * @param id - Family ID
     * @returns ResponseData with family
     */
    getByIdRaw(id) {
        const endpoint = this.getEndpoint('getById', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== FAMILY MEMBER MANAGEMENT ====================
    /**
     * Add a member to a family
     * POST /api/families/{familyId}/members
     * @param familyId - Family ID
     * @param request - Member and relationship information
     * @returns MemberInFamily record
     */
    async addMember(familyId, request) {
        const response = await this.addMemberRaw(familyId, request);
        if (!response.data) {
            throw new Error(`Failed to add member to family ${familyId}`);
        }
        return response.data;
    }
    /**
     * Add a member to a family (raw)
     * @param familyId - Family ID
     * @param request - Member and relationship information
     * @returns ResponseData with MemberInFamily
     */
    addMemberRaw(familyId, request) {
        const endpoint = this.getEndpoint('addMember', `${familyId}/members`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Remove a member from a family
     * DELETE /api/families/{familyId}/members/{memberId}
     * @param familyId - Family ID
     * @param memberId - Member ID to remove
     * @returns true if successful
     */
    async removeMember(familyId, memberId) {
        const response = await this.removeMemberRaw(familyId, memberId);
        return response.success;
    }
    /**
     * Remove a member from a family (raw)
     * @param familyId - Family ID
     * @param memberId - Member ID to remove
     * @returns ResponseData
     */
    removeMemberRaw(familyId, memberId) {
        const endpoint = this.getEndpoint('removeMember', `${familyId}/members/${memberId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
}
FamilyService.DEFAULT_SERVICE_PATH = 'members';
FamilyService.BASE_PATH = 'api/families';
//# sourceMappingURL=FamilyService.js.map